import "./index.scss";

import { Box, Button, ButtonGroup, Grid, Stack } from "@mui/material";
import BarChart from "components/CustomBarChart";
import RatingBar from "components/RatingBar";
import { DATE_FORMAT } from "constants/index";
import moment from "moment";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

const PFI = forwardRef(
  (
    props: {
      previousPFIs?: { [key: string]: string | number }[];
      currentTab: string;
      shareSubView?: Function;
    },
    ref
  ) => {
    const { currentTab, shareSubView, previousPFIs } = props;
    const [showGraph, setShowGraph] = useState(false);
    const [graphData, setGraphData] = useState<any[]>([]);
    const [graphColumns, setGraphColumns] = useState<any[]>([]);
    const formContext = useFormContext();
    const {
      control,
      getValues,
      setValue,
      watch,
      formState: { errors },
    } = formContext;
    const watchFeelings = watch([
      "pfiWeary",
      "pfiFatigued",
      "pfiWornOut",
      "pfiExhausted",
      "pfiTired",
      "pfiFullOfEnergy",
      "pfiActive",
      "pfiEnthusiastic",
      "pfiAlert",
      "pfiLively",
      "pfiTense",
      "pfiAnxious",
      "pfiRestless",
      "pfiNervous",
      "pfiJittery",
    ]);

    useEffect(() => {
      if (currentTab === "pfi" && !showGraph) {
        var values = watchFeelings.map((m) => (typeof m === "number" ? m : 0));
        const fatigue = values[0] + values[1] + values[2] + values[3] + values[4];
        const energy = values[5] + values[6] + values[7] + values[8] + values[9];
        const stress = values[10] + values[11] + values[12] + values[13] + values[14];

        setValue("fatigue_score", fatigue);
        setValue("energy_score", energy);
        setValue("stress_score", stress);
      }
    }, [currentTab, setValue, showGraph, watchFeelings, previousPFIs]);

    useImperativeHandle(ref, () => ({
      switchView(view: string) {
        if (view === "graph") {
          buildGraphData();
        } else {
          setShowGraph(false);
        }
      },
    }));

    const renderLegend = () => {
      return (
        <Stack direction="row" justifyContent="left" alignItems="center" ml={5} mt={1} gap={1}>
          <Stack direction="row" justifyContent="left" gap={1} alignItems="center">
            <Box className="legend-circle-view" bgcolor={"#4646ff"}></Box>
            Fatigue
          </Stack>
          <Stack direction="row" justifyContent="left" gap={1} alignItems="center">
            <Box className="legend-circle-view" bgcolor={"#00c400"}></Box>
            Energy
          </Stack>
          <Stack direction="row" justifyContent="left" gap={1} alignItems="center">
            <Box className="legend-circle-view" bgcolor={"#ff4646"}></Box>
            Stress
          </Stack>
        </Stack>
      );
    };

    //#region event handlers
    const buildGraphData = () => {
      //#region updating graphData
      let tempGraphColumns = [];
      let copyOfPreviousPFIs = [
        ...(previousPFIs || [{ pfi: "fatigue" }, { pfi: "energy" }, { pfi: "stress" }]),
      ];
      copyOfPreviousPFIs.find((f) => f.pfi === "fatigue")[
        "fatigue_" + moment().format(DATE_FORMAT)
      ] = getValues("fatigue_score");
      copyOfPreviousPFIs.find((f) => f.pfi === "energy")["energy_" + moment().format(DATE_FORMAT)] =
        getValues("energy_score");
      copyOfPreviousPFIs.find((f) => f.pfi === "stress")["stress_" + moment().format(DATE_FORMAT)] =
        getValues("stress_score");

      //#region Get columns from previous goals
      copyOfPreviousPFIs?.forEach((previousPFI) => {
        let columnColor = "";
        switch (previousPFI["pfi"]) {
          case "fatigue":
            columnColor = "#4646ff";
            break;
          case "energy":
            columnColor = "#00c400";
            break;
          case "stress":
            columnColor = "#ff4646";
            break;
        }
        Object.keys(previousPFI).forEach((key) => {
          if (key === "pfi") return;
          //Check if the key already exists in columns
          tempGraphColumns.push({
            color: columnColor,
            label: key.replace(`${previousPFI["pfi"]}_`, ""),
            name: `${key}`,
          });
          // if (!tempGraphColumns.find((f) => f.name === key)) {
          // }
        });
      });
      //#endregion Get columns from previous goals

      setGraphData([...copyOfPreviousPFIs]);
      setGraphColumns(tempGraphColumns);
      //#endregion updating graphData

      handleToggleShowGraph();
    };

    const handleToggleShowGraph = () => {
      if (shareSubView) {
        shareSubView(!showGraph);
      }
      setShowGraph(!showGraph);
    };
    //#endregion event handlers

    if (currentTab !== "pfi") return null;
    return (
      <Stack className="tcaPfi" flexGrow={1}>
        <Box width={`374px`} mb={`20px`}>
          <ButtonGroup className="button-group">
            <Button className={`${!showGraph ? `active` : ``}`}>Feeling Scale</Button>
            <Button
              className={`${showGraph ? `active` : ``}`}
              onClick={() => {
                toast("Click Next to view Feeling Graph", { type: "info" });
              }}
            >
              Feeling Graph
            </Button>
          </ButtonGroup>
        </Box>
        {!showGraph && (
          <>
            <h3>
              This is a survey that we will use to determine how your exercise program may be
              affecting your mood and energy levels. Below are some words and phrases that describe
              how people feel. These words may seem similar in nature so use whatever the word means
              for you is fine. Please fill in one response each, based on your feelings over the
              past two weeks, including today. Scale is:
            </h3>
            <Box className="ratingLegend">
              <Box>0 - Not at all</Box>
              <Box>1 - A little</Box>
              <Box>2 - Moderately</Box>
              <Box>3 - Quite a bit</Box>
              <Box>4 - Extremely</Box>
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Weary</Box>
                    <Box>
                      <Controller
                        name="pfiWeary"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiWeary?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Fatigued</Box>
                    <Box>
                      <Controller
                        name="pfiFatigued"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiFatigued?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Worn-out</Box>
                    <Box>
                      <Controller
                        name="pfiWornOut"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiWornOut?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Exhausted</Box>
                    <Box>
                      <Controller
                        name="pfiExhausted"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiExhausted?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Tired</Box>
                    <Box>
                      <Controller
                        name="pfiTired"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiTired?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Full of Energy</Box>
                    <Box>
                      <Controller
                        name="pfiFullOfEnergy"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiFullOfEnergy?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Active</Box>
                    <Box>
                      <Controller
                        name="pfiActive"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiActive?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Enthusiastic</Box>
                    <Box>
                      <Controller
                        name="pfiEnthusiastic"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiEnthusiastic?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Alert</Box>
                    <Box>
                      <Controller
                        name="pfiAlert"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiAlert?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Lively</Box>
                    <Box>
                      <Controller
                        name="pfiLively"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiLively?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Tense</Box>
                    <Box>
                      <Controller
                        name="pfiTense"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiTense?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Anxious</Box>
                    <Box>
                      <Controller
                        name="pfiAnxious"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiAnxious?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Restless</Box>
                    <Box>
                      <Controller
                        name="pfiRestless"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiRestless?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Nervous</Box>
                    <Box>
                      <Controller
                        name="pfiNervous"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiNervous?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
                <Box className="rowQuestionBox">
                  <Stack direction={"row"} alignItems="center">
                    <Box flexGrow={1}>Jittery</Box>
                    <Box>
                      <Controller
                        name="pfiJittery"
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { onChange, value, ref } }) => (
                          <Stack spacing={1} flexGrow={1}>
                            <RatingBar value={value} onChange={onChange} startFromZero />
                            {errors.pfiJittery?.type === "required" && (
                              <h4 className="error">This field is required</h4>
                            )}
                          </Stack>
                        )}
                      />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            </Grid>
          </>
        )}
        {showGraph && (
          <Box height={"400px"}>
            <BarChart
              data={graphData}
              xAxisDataKey="pfi"
              bars={graphColumns}
              legendRenderCallback={() => renderLegend()}
            />
          </Box>
        )}
      </Stack>
    );
  }
);

export default PFI;
